/**
 * MyGrievancesCard.jsx — Feb 2026. Small "My Grievances" widget for the
 * Dashboard's People tab. Employees can see the status of their own past
 * anonymous submissions (GET /grievances/mine, matched server-side via a
 * one-way hash — no user_id is ever stored) without needing the
 * admin-gated My HR → Grievances tab. Shares the submit dialog with the
 * sticky header's "Submit Grievance" quick action.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/dashboard/DashboardCards";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import GrievanceSubmitDialog from "@/components/GrievanceSubmitDialog";
import { formatDate } from "@/lib/dateFormat";
import { ShieldAlert, Plus } from "lucide-react";

function statusTone(s) {
  if (s === "open") return "orange";
  if (s === "resolved") return "green";
  return "slate";
}

export default function MyGrievancesCard() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => { api.get("/grievances/mine").then(({ data }) => setRows(data || [])).catch(() => setRows([])); };
  useEffect(() => {
    load();
    // Feb 2026 — refetch when a grievance is submitted from ANY dialog
    // instance (e.g. the sticky header's quick-action, not just this
    // card's own "Submit" button).
    window.addEventListener("grievance:submitted", load);
    return () => window.removeEventListener("grievance:submitted", load);
  }, []);

  return (
    <>
      <Card title="My Grievances" hint="Private to you" icon={ShieldAlert} testId="card-my-grievances">
        <div className="flex items-center justify-end mb-2">
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setOpen(true)} data-testid="my-grievances-submit-btn"><Plus size={12} className="mr-1" /> Submit</Button>
        </div>
        {rows === null ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="my-grievances-card-empty">Nothing raised yet.</div>
        ) : (
          <ul className="space-y-1.5 text-sm max-h-64 overflow-y-auto pr-1" data-testid="my-grievances-card-list">
            {rows.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-2" data-testid={`my-grievances-card-row-${g.id}`}>
                <span className="text-slate-700 truncate">{g.title}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <Tag tone={statusTone(g.status)}>{g.status === "in_progress" ? "working" : g.status}</Tag>
                  <span className="text-[10px] text-slate-400">{formatDate(g.created_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <GrievanceSubmitDialog open={open} onOpenChange={setOpen} onSubmitted={load} />
    </>
  );
}
